import { html, nothing, TemplateResult } from 'lit';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import {
  WEB_COMPONENT_APPLICATION_CVE_LIST,
  WEB_COMPONENT_INLINE_ROW_ACTIONS,
} from '../../../../webcomponents.constants';
import { InlineRowActionsWebComponent } from '../../../../grid/uiElements/inlineRowActions';
import { EASMActions, FindingDetailsPayload, FindingGroup, RelatedFinding } from '../../findingDetails/types';
import { createRows } from '../../utils';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip';
import { BaseSpecificDetailsView } from '../../specificDetailViewByType/baseSpecificDetailsView';
import { ApplicationCveListWebComponent } from '../../../../applicationCveList';
import { UNAVAILABLE_DATA } from '../../../types';

export class ServicesView extends BaseSpecificDetailsView {
  static getGeneralInfo(findingDetails: FindingDetailsPayload, dependencies: any): TemplateResult {
    const menuBtn = window.document.createElement(WEB_COMPONENT_INLINE_ROW_ACTIONS) as InlineRowActionsWebComponent;
    menuBtn.inlineActions = [
      {
        title: translateFn('Copy to clipboard'),
        value: EASMActions.COPY_TO_CLIPBOARD_IP,
      },
      {
        title: translateFn('View incidents'),
        value: EASMActions.VIEW_INCIDENTS_IP,
      },
    ];

    if (!findingDetails.canPivotToIncidents) {
      menuBtn.disabled = true;
    }

    const menuBtnTooltip = new GzTooltipWebComponent();
    menuBtnTooltip.tooltipText = translateFn('EDR license required');
    menuBtnTooltip.appendChild(menuBtn);

    const detailsInfo = [
      {
        label: translateFn('Port'),
        value: findingDetails.port,
      },
      {
        label: translateFn('Protocol'),
        value: findingDetails.protocol,
      },
    ];

    if (findingDetails.version) {
      detailsInfo.push({
        label: translateFn('Version'),
        value: findingDetails.version,
      });
    }

    const rows = createRows(detailsInfo, UNAVAILABLE_DATA);
    return html`
      <gz-labeled-content label="${translateFn('IP')}" class="labeled-row" labelFontSize="14">
        <div class="d-flex align-items-center item-with-action">
          <gz-tooltip tooltipText="${findingDetails.ip ?? UNAVAILABLE_DATA}" isEllipsis>${findingDetails.ip ?? UNAVAILABLE_DATA}</gz-tooltip>
          ${findingDetails.ip ? (findingDetails.canPivotToIncidents ? menuBtn : menuBtnTooltip) : nothing}
        </div>
      </gz-labeled-content>
      ${rows}
    `;
  }

  static getGeneralInfoBottom(findingDetails: FindingDetailsPayload, dependencies: any): TemplateResult {
    let cveList: any = html` <div class="container">
      <div class="section-title fs-12 bordered">${translateFn('CVE ID (0)')}</div>
      ${translateFn('No CVEs found')}
    </div>`;
    if (findingDetails.cvesDetailedList && findingDetails.cvesDetailedList.length > 0) {
      cveList = window.document.createElement(WEB_COMPONENT_APPLICATION_CVE_LIST) as ApplicationCveListWebComponent;
      cveList.cveList = findingDetails.cvesDetailedList;
      cveList.companyId = findingDetails.companyId;
      cveList.isVersionVisible = false;
      cveList.hasViewDevicesButtonVisible = true;
    }
    return cveList;
  }

  static getSubtitle(relatedFindings: FindingGroup): string {
    let findingsWithVulnerabilities = 0;
    for (const finding of relatedFindings.items) {
      if (finding.cves.length > 0) {
        findingsWithVulnerabilities += findingsWithVulnerabilities;
      }
    }

    let openPortSubtitleSection = translateFn('{ports}/{ports} open ports', {
      ports: relatedFindings.count,
    });
    if (relatedFindings.items.length > 1) {
      openPortSubtitleSection = translateFn('1/1 open port');
    }

    let vulnerabilitiesSubtitleSection = '';
    if (findingsWithVulnerabilities === 1) {
      vulnerabilitiesSubtitleSection = translateFn('1/1 has a vulnerability');
    } else {
      vulnerabilitiesSubtitleSection = translateFn('{vulnerabilities}/{total} have vulnerabilities', {
        vulnerabilities: findingsWithVulnerabilities,
        total: relatedFindings.items.length,
      });
    }
    return openPortSubtitleSection + ', ' + vulnerabilitiesSubtitleSection;
  }

  static getRelatedInfo(relatedFinding: RelatedFinding): TemplateResult | typeof nothing {
    return html`
      <gz-text class="finding-detail" fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}">
        ${translateFn('1 open port')}
      </gz-text>

      ${relatedFinding.cves.length
        ? html`
            <gz-text class="finding-detail" fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}">
              ${1 === relatedFinding.cves.length
                ? translateFn('1 CVE')
                : translateFn('{count} CVEs', { count: relatedFinding.cves.length })}
            </gz-text>
          `
        : nothing}
    `;
  }
}
